import {
  em,
  getBreakpointValue,
  getSpacing,
  InlineStyles,
  Box as MantineBox,
  Center as MantineCenter,
  Container as MantineContainer,
  Flex as MantineFlex,
  Grid as MantineGrid,
  Group as MantineGroup,
  SimpleGrid as MantineSimpleGrid,
  Space as MantineSpace,
  Stack as MantineStack,
  rem,
  useRandomClassName,
} from "@mantine/core";
import classNames from "classnames";
import * as React from "react";
import { tokens } from "../tokens";
import classes from "./layout.module.css";
import type { ElementProps, ResponsiveCssProperty, Size, StyleProps } from "./style-props";

type LayoutSize = Size;

type DivProps = ElementProps<"div">;

interface LayoutProps extends DivProps, StyleProps {}

export interface FlexProps extends LayoutProps {
  gap?: ResponsiveCssProperty<"gap">;
  rowGap?: ResponsiveCssProperty<"rowGap">;
  columnGap?: ResponsiveCssProperty<"columnGap">;
  align?: ResponsiveCssProperty<"alignItems">;
  justify?: ResponsiveCssProperty<"justifyContent">;
  wrap?: ResponsiveCssProperty<"flexWrap">;
  direction?: ResponsiveCssProperty<"flexDirection">;
}

export const Flex = React.forwardRef<HTMLDivElement, FlexProps>(function Flex({ children, ...props }, ref) {
  return (
    <MantineFlex {...props} ref={ref}>
      {children}
    </MantineFlex>
  );
});

export interface StackProps extends LayoutProps {
  spacing?: LayoutSize;
  align?: React.CSSProperties["alignItems"];
  justify?: React.CSSProperties["justifyContent"];
}

export const Stack = React.forwardRef<HTMLDivElement, StackProps>(function Stack({ children, spacing, ...props }, ref) {
  return (
    <MantineStack {...props} gap={spacing} ref={ref}>
      {children}
    </MantineStack>
  );
});

export interface GroupProps extends LayoutProps {
  position?: "left" | "center" | "right" | "apart";
  noWrap?: boolean;
  grow?: boolean;
  spacing?: LayoutSize;
  align?: React.CSSProperties["alignItems"];
}

/** Mantine 6's Group `position` values as the Mantine 7 `justify` they meant. */
const GROUP_JUSTIFY: Record<NonNullable<GroupProps["position"]>, React.CSSProperties["justifyContent"]> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
  apart: "space-between",
};

export const Group = React.forwardRef<HTMLDivElement, GroupProps>(function Group({ children, position, noWrap, spacing, ...props }, ref) {
  return (
    <MantineGroup
      {...props}
      gap={spacing}
      justify={position === undefined ? undefined : GROUP_JUSTIFY[position]}
      wrap={noWrap ? "nowrap" : undefined}
      ref={ref}
    >
      {children}
    </MantineGroup>
  );
});

export type BoxProps = LayoutProps;

export const Box = React.forwardRef<HTMLDivElement, BoxProps>(function Box({ children, ...props }, ref) {
  return (
    <MantineBox {...props} ref={ref}>
      {children}
    </MantineBox>
  );
});

export interface ContainerProps extends LayoutProps {
  size?: LayoutSize;
  fluid?: boolean;
}

/** A Container's resolved `w`, as the custom property the `containerWidth` rule reads. */
type ContainerWidthVariable = React.CSSProperties & Record<"--sc-container-width", string | undefined>;

interface ContainerWidth {
  /** A single value, written on the element. */
  inline?: ContainerWidthVariable;
  /** A responsive value: its base and one entry per breakpoint, written as rules on a generated class. */
  rules?: { base?: ContainerWidthVariable; media: { query: string; styles: ContainerWidthVariable }[] };
}

type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";

/** The theme breakpoints, narrowest first: the order Mantine 7 writes a responsive style prop's media rules in. */
const BREAKPOINTS: Breakpoint[] = ["xs", "sm", "md", "lg", "xl"];

/**
 * Mantine 7's spacing resolver, which is how it resolves `w`: a theme spacing key (the theme's spacing is
 * `tokens.spacing`) becomes its variable, negated for a leading `-`; anything else goes through Mantine's `rem`.
 */
const resolveWidth = (value: string | number | undefined): string | undefined => {
  if (typeof value !== "string") {
    return value === undefined ? undefined : rem(value);
  }

  const key = value.replace("-", "");
  if (!(key in tokens.spacing)) {
    return rem(value);
  }
  return value.startsWith("-") ? `calc(var(--mantine-spacing-${key}) * -1)` : `var(--mantine-spacing-${key})`;
};

const widthVariable = (value: string | number | undefined): ContainerWidthVariable => ({ "--sc-container-width": resolveWidth(value) });

/**
 * A Container's `w` where Mantine 7 would have written `width`, and resolved as it would have been: a single
 * value (or a responsive one holding only `base`) inline, and any other responsive value as a rule for its
 * truthy base plus one per breakpoint key at that theme breakpoint's `min-width` (`parse-style-props.mjs`).
 */
const containerWidth = (width: ContainerProps["w"]): ContainerWidth => {
  if (typeof width !== "object") {
    return width === undefined ? {} : { inline: widthVariable(width) };
  }

  const keys = Object.keys(width);
  if (keys.length === 1 && keys[0] === "base") {
    return width.base === undefined ? {} : { inline: widthVariable(width.base) };
  }

  return {
    rules: {
      base: width.base ? widthVariable(width.base) : undefined,
      media: BREAKPOINTS.filter(breakpoint => breakpoint in width).map(breakpoint => ({
        query: `(min-width: ${tokens.breakpoint[breakpoint]})`,
        styles: widthVariable(width[breakpoint]),
      })),
    },
  };
};

/**
 * Mantine 6 emitted `w` as an Emotion class prepended to the document head, so the print width
 * (`print.css` `[data-sc-print-wide]`, equal specificity, later) won over it; Mantine 7 writes `w` inline,
 * which wins over every rule. The wrapper resolves `w` as Mantine 7 does into `--sc-container-width` and the
 * single-class `containerWidth` rule applies it, so the print rule wins again (SC-2703). A caller's `style`
 * width now wins over `w`, as it did in Mantine 6; Mantine 7 let the style prop win.
 */
export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(function Container(
  { children, className, style, w, ...props },
  ref,
) {
  const responsiveClassName = useRandomClassName();
  const { inline, rules } = containerWidth(w);
  const hasWidth = inline !== undefined || rules !== undefined;
  const containerStyle: React.CSSProperties | undefined = inline === undefined ? style : { ...style, ...inline };

  return (
    <>
      {rules && <InlineStyles selector={`.${responsiveClassName}`} styles={rules.base} media={rules.media} />}
      <MantineContainer
        {...props}
        className={hasWidth ? classNames(className, classes.containerWidth, rules && responsiveClassName) : className}
        style={containerStyle}
        data-sc-print-wide="true"
        ref={ref}
      >
        {children}
      </MantineContainer>
    </>
  );
});

export interface CenterProps extends LayoutProps {
  inline?: boolean;
}

export const Center = React.forwardRef<HTMLDivElement, CenterProps>(function Center({ children, ...props }, ref) {
  return (
    <MantineCenter {...props} ref={ref}>
      {children}
    </MantineCenter>
  );
});

export interface GridProps extends LayoutProps {
  gutter?: LayoutSize;
  gutterXs?: LayoutSize;
  gutterSm?: LayoutSize;
  gutterMd?: LayoutSize;
  gutterLg?: LayoutSize;
  gutterXl?: LayoutSize;
  grow?: boolean;
  justify?: React.CSSProperties["justifyContent"];
  align?: React.CSSProperties["alignContent"];
  columns?: number;
}

export interface GridColProps extends DivProps {
  span?: number | "auto" | "content";
  offset?: number;
  order?: number;
  xs?: number | "auto" | "content";
  sm?: number | "auto" | "content";
  md?: number | "auto" | "content";
  lg?: number | "auto" | "content";
  xl?: number | "auto" | "content";
}

/**
 * Mantine 6's per-breakpoint props as one Mantine 7 responsive value. Both apply at `min-width` of the
 * theme breakpoint; a value with no breakpoint override stays scalar so its markup is unchanged.
 */
const responsive = <T,>(base: T | undefined, overrides: Partial<Record<Breakpoint, T | undefined>>) => {
  const defined = Object.entries(overrides).filter(([, value]) => value !== undefined);
  return defined.length === 0 ? base : { ...(base === undefined ? {} : { base }), ...Object.fromEntries(defined) };
};

/** Mantine 6 sized an unspanned column to the grid's full column count; Mantine 7 defaults it to 12. */
const GridColumnsContext = React.createContext(12);

/** The flex-container properties of a caller's Grid `style`. */
const GRID_ROW_STYLE_KEYS = new Set([
  "alignContent",
  "alignItems",
  "columnGap",
  "flexDirection",
  "flexFlow",
  "flexWrap",
  "gap",
  "justifyContent",
  "placeContent",
  "placeItems",
  "rowGap",
]);

/**
 * Mantine 6's Grid root was itself the flex row; Mantine 7 renders the row as an inner element inside
 * a plain root. The flex-container keys of a caller's `style` (the certs form aligns its row with
 * `alignItems`) therefore go to that inner row, where Mantine 6 applied them, and every other key stays
 * on the root (SC-2703). A style with no such key reaches the root unchanged.
 */
const splitGridStyle = (style: React.CSSProperties | undefined): { root?: React.CSSProperties; row?: React.CSSProperties } => {
  const entries = Object.entries(style ?? {});
  if (!entries.some(([key]) => GRID_ROW_STYLE_KEYS.has(key))) {
    return { root: style };
  }

  return {
    root: Object.fromEntries(entries.filter(([key]) => !GRID_ROW_STYLE_KEYS.has(key))),
    row: Object.fromEntries(entries.filter(([key]) => GRID_ROW_STYLE_KEYS.has(key))),
  };
};

const GridRoot = React.forwardRef<HTMLDivElement, GridProps>(function GridRoot(
  { children, gutter, gutterXs, gutterSm, gutterMd, gutterLg, gutterXl, columns = 12, style, ...props },
  ref,
) {
  const { root, row } = splitGridStyle(style);

  return (
    <GridColumnsContext.Provider value={columns}>
      <MantineGrid
        {...props}
        style={root}
        styles={row === undefined ? undefined : { inner: row }}
        columns={columns}
        gutter={responsive(gutter, { xs: gutterXs, sm: gutterSm, md: gutterMd, lg: gutterLg, xl: gutterXl })}
        ref={ref}
      >
        {children}
      </MantineGrid>
    </GridColumnsContext.Provider>
  );
});

const GridColumn = React.forwardRef<HTMLDivElement, GridColProps>(function GridColumn(
  { children, span, xs, sm, md, lg, xl, ...props },
  ref,
) {
  const columns = React.useContext(GridColumnsContext);

  return (
    <MantineGrid.Col {...props} span={responsive(span || columns, { xs, sm, md, lg, xl })} ref={ref}>
      {children}
    </MantineGrid.Col>
  );
});

export const Grid = Object.assign(GridRoot, { Col: GridColumn });

export interface SimpleGridBreakpoint {
  maxWidth: React.CSSProperties["maxWidth"];
  cols: number;
  spacing?: LayoutSize;
}

export interface SimpleGridProps extends LayoutProps {
  breakpoints?: SimpleGridBreakpoint[];
  cols?: number;
  spacing?: LayoutSize;
  verticalSpacing?: LayoutSize;
}

/**
 * Mantine 7 replaced the desktop-first `breakpoints` list with mobile-first responsive props keyed by
 * theme breakpoint, which cannot express an arbitrary `maxWidth`. The wrapper keeps Mantine 6's own
 * rules instead: each entry applies below its width (`max-width: width - 1px`), widest first so the
 * narrower entry wins, and sets the column count, the entry's column gap and the grid's row gap. A
 * breakpoint key resolves through `tokens.breakpoint`, the theme's breakpoints.
 */
export const SimpleGrid = React.forwardRef<HTMLDivElement, SimpleGridProps>(function SimpleGrid(
  { children, breakpoints = [], className, cols = 1, spacing = "md", verticalSpacing, ...props },
  ref,
) {
  const breakpointClassName = useRandomClassName();
  const media = breakpoints
    .map(breakpoint => ({ breakpoint, width: getBreakpointValue(breakpoint.maxWidth ?? 0, tokens.breakpoint) }))
    .sort((left, right) => right.width - left.width)
    .map(({ breakpoint, width }) => {
      const styles: React.CSSProperties & Record<`--sg-${string}`, string | undefined> = {
        "--sg-cols": String(breakpoint.cols),
        "--sg-spacing-x": getSpacing(breakpoint.spacing ?? spacing),
        "--sg-spacing-y": getSpacing(verticalSpacing ?? spacing),
      };
      return { query: `(max-width: ${em(width - 1)})`, styles };
    });

  return (
    <>
      <MantineSimpleGrid
        {...props}
        className={media.length === 0 ? className : [className, breakpointClassName].filter(Boolean).join(" ")}
        cols={cols}
        spacing={spacing}
        verticalSpacing={verticalSpacing}
        ref={ref}
      >
        {children}
      </MantineSimpleGrid>
      {media.length > 0 && <InlineStyles selector={`.${breakpointClassName}.${breakpointClassName}`} media={media} />}
    </>
  );
});

/** Empty layout space sized by the shared style-prop vocabulary. */
export type SpaceProps = StyleProps;

export const Space = React.forwardRef<HTMLDivElement, SpaceProps>(function Space(props, ref) {
  return <MantineSpace {...props} ref={ref} />;
});
